
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import ScheduleTabs from './components/schedule/ScheduleTabs';
import { API } from './config/constants';
import { getFacilityId, convertTo24Hour } from './utils/format';
import "./components/schedule/ScheduleFetcher.css"
import { useNavigate } from 'react-router-dom';

const ScheduleFetcher = () => {
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState('1');
  const [date, setDate] = useState("");
  const [schedule, setSchedule] = useState([]);
  const [error, setError] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const facilityId = getFacilityId()

  useEffect(() => {

    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    setDate(formattedDate);
  }, []);

  // Fetch rooms when component mounts
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const { data } = await axios.post(API.GET_CATEGORIES, { facility_id: facilityId });
       const rooms = (data?.rooms || []).filter((room) => room.status !== 'private' && room.name !== 'On-Demand');

      // Sort rooms by id ascending
      rooms.sort((a, b) => a.id - b.id);

      setRooms(rooms);
      } catch {
        setError('Failed to load rooms');
      }
    };
    fetchRooms();
  }, [facilityId]);

  const fetchScheduleRequestId = useRef(0);

  const fetchSchedule = useCallback(async () => {
    if (!selectedRoomId || !date) return;

    const requestId = ++fetchScheduleRequestId.current;
    setLoading(true);
    try {
      const { data } = await axios.post(API.GET_DAY_SCHEDULE, {
        facility_id: facilityId,
        room_id: selectedRoomId,
        date,
      });
      // Ignore this response if a newer fetchSchedule call has since been issued,
      // so a slower older request can't overwrite fresher state (e.g. after a
      // check-in or add-member triggers a refetch while a prior one is still in flight).
      if (requestId !== fetchScheduleRequestId.current) return;
      setSchedule(data?.schedule || []);
    } catch {
      if (requestId !== fetchScheduleRequestId.current) return;
      setError('Error fetching schedule');
    } finally {
      if (requestId === fetchScheduleRequestId.current) setLoading(false);
    }
  }, [facilityId, selectedRoomId, date]);



  const navigate = useNavigate();

  // Fetch schedule data when room is selected
  useEffect(() => {
    if (!selectedRoomId) return;
    fetchSchedule();
  }, [selectedRoomId, date]);

  const handleTabClick = useCallback((roomId) => {
    setSelectedRoomId(roomId);
    setSchedule([]);
    setError('');
    setSelectedIndex(0);
  }, []);

  const handleTimeTabChange = useCallback((index) => {
    setSelectedIndex(index);
  }, []);


  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/login');
  }, [navigate]);


  const handleAddMember = useCallback(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const handleCheckIn = useCallback(async (calendarScheduleId) => {
    try {
      await axios.post(API.UPDATE_CHECKIN_COUNT, {
        facility_id: facilityId,
        calendar_schedule_id: calendarScheduleId,
      });
      fetchSchedule();
    } catch {
      setError('Error updating check-in');
    }
  }, [facilityId, fetchSchedule]);




  const customStyles = {
    container: {
      maxWidth: '1200px', // For example, max-width of 1200px
      margin: '0 auto',
      padding: '16px',
      position: 'relative',
      marginTop: '-56px',
    },
    button: {
      padding: '8px 30px',
      backgroundColor: 'white',
      // borderRadius: '34px 34px 0 0',
      fontWeight: '500',
      fontSize: '14px',
      color: 'black',
      boxShadow: 'rgba(0, 0, 0, 0.1) 0px -4px 10px',
      clipPath: 'polygon(10% 0%, 90% 0%, 100% 100%, 0% 100%)',
    }
  };

  const sortedSchedule = useMemo(() => {
    return schedule
      .filter(Boolean)
      .sort((a, b) => convertTo24Hour(a.start_time) - convertTo24Hour(b.start_time));
  }, [schedule]);


  return (
    <>

      <div className="w-full bg-[#e00000] text-white py-5 pt-5 relative min-h-[233px]">
        <div className="max-w-4xl mx-auto flex justify-between items-center relative mb-20 px-4 mt-[59px]">
          {sortedSchedule && sortedSchedule.length > 0 && sortedSchedule[selectedIndex] != null && (
            <div className="text-[36.56px] font-bold  min-w-[400px] max-w-[50%] text-left">{sortedSchedule[selectedIndex].class_name} <span className="font-normal text-[22px]">({sortedSchedule[selectedIndex].start_time} - {sortedSchedule[selectedIndex].end_time})</span> </div>
          )}

          <div onClick={handleLogout} className='font-normal text-[22px] items-end cursor-pointer absolute right-[36px]'>Logout</div>

        </div>
      </div>
      <div className="container max-w-4xl mx-auto p-4 relative mt-[-49px]">
        {/* Room Tabs */}
        <div className="flex mb-6">
          {rooms.map((room) => (
            // <button
            //   key={room.id}
            //   style={customStyles.button}
            //   onClick={() => handleTabClick(room.id)}
            //   className={`px-4 py-2 text-lg font-medium ${selectedRoomId === room.id ? '!bg-white' : '!bg-[#e0e1e2]'
            //     }`}
            // >
            //   {room.name}
            // </button>
             <button
                key={room.id}
                onClick={() => handleTabClick(room.id)}
                className="relative h-[34px] min-w-39.75  w-fit px-4 focus:outline-none cursor-pointer"
              >
                {/* SVG background */}
                <svg
                  viewBox="0 0 124.406 43.385"
                  preserveAspectRatio="none"
                  className={`absolute inset-0 h-full w-full transition-colors
                    ${selectedRoomId === room.id  ? 'fill-white' : 'fill-transparent'}
                  `}
                >
                  <g transform="translate(154.32) rotate(90)">
                    <g transform="translate(0 2.344)">
                      <path d="M0,129.824v-80.1A15.584,15.584,0,0,1,12.4,34.782l30.989-7.212V151.976L12.4,144.764A15.585,15.585,0,0,1,0,129.824" />
                    </g>
                  </g>
                </svg>

                {/* Button text */}
                <span
                  className={`relative z-10 flex h-full w-full items-center justify-center sm:text-[14px] text-[10px] font-semibold tracking-wide
                    ${selectedRoomId === room.id  ? 'text-black' : 'text-white'}
                  `}
                
                >
                  {room.name}
                </span>
              </button>
          ))}
        </div>

        {/* Display the result */}
        {error && <div className="text-red-500 mt-4 text-center">{error}</div>}
        {sortedSchedule.length > 0 && (
          <ScheduleTabs
            schedule={sortedSchedule}
            onAddMember={handleAddMember}
            onCheckinCountUpdated={handleCheckIn}
            onTabChange={handleTimeTabChange}
          />
        )}

        {selectedRoomId && !loading && !error && sortedSchedule && sortedSchedule.length === 0  && sortedSchedule[0] != "null" && (
          <div className="text-left text-gray-500">There are no sessions scheduled today.</div>
        )}
      </div>
    </>

  );
};

export default ScheduleFetcher;
